import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { apiCall } from './useAutoSave';
import RecipeHeader from './RecipeHeader';
import StepTimeline from './StepTimeline';
import StepDetail from './StepDetail';
import NeprirazenePanel from './NeprirazenePanel';

// Virtuální „krok" pro ingredience bez sop_krok_id (viz NeprirazenePanel).
const NEPRIRAZENO = '__neprirazeno__';

// Porovnání hodnoty pole s baseline — null/undefined/'' se považují za shodné,
// pole (stroj_ids) porovnáváme po setřídění.
const sameVal = (a, b) => {
    if (Array.isArray(a) || Array.isArray(b)) {
        const sa = [...(a || [])].map(Number).sort((x, y) => x - y).join(',');
        const sb = [...(b || [])].map(Number).sort((x, y) => x - y).join(',');
        return sa === sb;
    }
    const na = a == null ? '' : a;
    const nb = b == null ? '' : b;
    return na === nb;
};

const baseStrojIds = (step) => Array.isArray(step?.stroje)
    ? step.stroje.map(s => s.id)
    : (step?.stroj_id ? [step.stroj_id] : []);

export default function RecepturaEditor({ recept, isDark, stroje, jednotky, kalkulace, onReceptUpdate, onCostChanged, onKalkulacePreview, onDirtyChange, onNavigate, saveRef }) {
    const krokCosts = {};
    (kalkulace?.per_krok || []).forEach(k => { krokCosts[k.sop_krok_id] = k; });
    const [steps, setSteps] = useState([]);
    const [bomItems, setBomItems] = useState([]);
    const [activeStepId, setActiveStepId] = useState(null);
    const [activeTab, setActiveTab] = useState('postup');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [localRecept, setLocalRecept] = useState(recept);

    // ── Draft buffer (přežije přepnutí záložek/kroků; zmizí až uložením / opuštěním položky) ──
    const [draftRecept, setDraftRecept] = useState({});   // { pocet_porci?, jednotka_id? }
    const [draftSteps, setDraftSteps] = useState({});     // { [stepId]: { nazev?, popis?, cas_s?, ... } }

    // ── Draft ingrediencí (working copy) ──
    // bomItems je pracovní kopie; změny se NEUKLÁDAJÍ hned, ale až přes Uložit.
    // Nové položky mají dočasné string ID `tmp_N`. Reconcile na uložení porovná
    // proti bomBaselineRef (poslední serverový stav).
    const bomBaselineRef = useRef([]);
    const [bomDirty, setBomDirty] = useState(false);
    const bomDirtyRef = useRef(false);
    bomDirtyRef.current = bomDirty;
    const bomItemsRef = useRef([]);
    bomItemsRef.current = bomItems;
    const tmpIdRef = useRef(0);
    const nextTmpId = () => `tmp_${++tmpIdRef.current}`;

    // Reset draftu při přepnutí položky (ref synchronně, ať následný load
    // natáhne ingredience nového receptu a nezachová staré drafty).
    useEffect(() => { setDraftRecept({}); setDraftSteps({}); setBomDirty(false); bomDirtyRef.current = false; }, [recept?.id]);

    useEffect(() => { setLocalRecept(recept); }, [recept?.id]);

    const loadData = useCallback(async ({ silent = false, noSpinner = false, aliveRef } = {}) => {
        if (!recept?.id) return;
        if (!noSpinner) setLoading(true);
        try {
            const [detail, bom] = await Promise.all([
                apiCall(`/api/recepty/recepty/${recept.id}`),
                apiCall(`/api/recepty/recepty/${recept.id}/kusovnik`),
            ]);
            if (aliveRef && !aliveRef.alive) return;
            const kroky = detail?.sop?.kroky || [];
            setSteps(kroky);
            // Ingredience přepiš ze serveru JEN když nemáme neuložené drafty —
            // jinak by reload (přidání kroku, média…) smazal rozeditované ingredience.
            if (!bomDirtyRef.current) {
                setBomItems(bom || []);
                bomBaselineRef.current = bom || [];
            }
            setLocalRecept(detail);
            if (onReceptUpdate) onReceptUpdate(detail, { silent });
            if (kroky.length > 0) setActiveStepId(prev => prev || kroky[0].id);
        } catch {}
        if (!noSpinner && (!aliveRef || aliveRef.alive)) setLoading(false);
    }, [recept?.id]);

    useEffect(() => {
        const ref = { alive: true };
        loadData({ silent: true, aliveRef: ref });
        return () => { ref.alive = false; };
    }, [loadData]);

    const costChangedRef = useRef(onCostChanged);
    costChangedRef.current = onCostChanged;
    const fireCostChanged = useCallback(() => costChangedRef.current?.(), []);

    // ── Draft helpers ──────────────────────────────────────────────
    const patchReceptDraft = useCallback((patch) => {
        setDraftRecept(prev => {
            const cur = { ...prev, ...patch };
            const pruned = {};
            for (const [k, v] of Object.entries(cur)) {
                if (!sameVal(v, localRecept?.[k])) pruned[k] = v;
            }
            return pruned;
        });
    }, [localRecept]);

    const patchStepDraft = useCallback((stepId, patch) => {
        setDraftSteps(prev => {
            const base = steps.find(s => s.id === stepId) || {};
            const cur = { ...(prev[stepId] || {}), ...patch };
            const pruned = {};
            for (const [k, v] of Object.entries(cur)) {
                const baseVal = k === 'stroj_ids' ? baseStrojIds(base) : base[k];
                if (!sameVal(v, baseVal)) pruned[k] = v;
            }
            const next = { ...prev };
            if (Object.keys(pruned).length) next[stepId] = pruned; else delete next[stepId];
            return next;
        });
    }, [steps]);

    // ── Draft ingrediencí — mutace jen do pracovní kopie (uloží se přes Uložit) ──
    const addBom = useCallback((searchItem, stepId) => {
        const it = {
            id: nextTmpId(),
            surovina_id: searchItem.typ === 'MAT' ? searchItem.id : null,
            ingredience_recept_id: searchItem.typ === 'SEM' ? searchItem.id : null,
            obal_id: searchItem.typ === 'OBA' ? searchItem.id : null,
            spotrebni_zbozi_id: searchItem.typ === 'SPOZ' ? searchItem.id : null,
            spotreba: 1,
            jednotka_id: searchItem.jednotka_id || (jednotky?.[0]?.id || 1),
            scrap_pct: 0,
            sop_krok_id: stepId,
            _label: searchItem.nazev,
            _kod: searchItem.kod,
            // Draft řádek nemá relace — bez tohohle by značky alergenů naskočily
            // až po uložení a přenačtení kusovníku.
            _alergeny: searchItem.alergeny || [],
        };
        setBomItems(prev => [...prev, it]);
        setBomDirty(true);
    }, [jednotky]);
    const updateBom = useCallback((id, patch) => {
        setBomItems(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
        setBomDirty(true);
    }, []);
    const deleteBom = useCallback((id) => {
        setBomItems(prev => prev.filter(b => b.id !== id));
        setBomDirty(true);
    }, []);
    const reorderBom = useCallback((stepId, orderedIds) => {
        setBomItems(prev => {
            const others = prev.filter(b => (b.sop_krok_id ?? null) !== (stepId ?? null));
            const inStep = orderedIds.map(id => prev.find(b => b.id === id)).filter(Boolean);
            return [...others, ...inStep];
        });
        setBomDirty(true);
    }, []);
    // Přesun nepřiřazených ingrediencí pod konkrétní krok
    const presunNeprirazene = useCallback((stepId) => {
        setBomItems(prev => prev.map(b => (b.sop_krok_id ? b : { ...b, sop_krok_id: stepId })));
        setBomDirty(true);
        setActiveStepId(stepId);
    }, []);

    const isDirty = Object.keys(draftRecept).length > 0 || Object.keys(draftSteps).length > 0 || bomDirty;

    // Nahlásit dirty stav rodiči (leave-guard) + varování při zavření okna
    useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);
    // Reset dirty příznaku rodiče při odmountování editoru
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
    useEffect(() => {
        if (!isDirty) return;
        const h = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [isDirty]);

    const saveAll = useCallback(async () => {
        if (saving || !isDirty) return;
        setSaving(true);
        try {
            if (Object.keys(draftRecept).length) {
                await apiCall(`/api/recepty/recepty/${recept.id}`, 'PATCH', draftRecept);
            }
            for (const [id, patch] of Object.entries(draftSteps)) {
                const base = steps.find(s => s.id === Number(id)) || {};
                const m = { ...base, ...patch };
                const stroj_ids = Array.isArray(m.stroj_ids) ? m.stroj_ids : baseStrojIds(m);
                await apiCall(`/api/recepty/sop/kroky/${id}`, 'PUT', {
                    krok: m.krok, nazev: m.nazev, popis: m.popis ?? null,
                    kriticky_bod: !!m.kriticky_bod, cas_s: m.cas_s ?? 0,
                    // null = krok pokrývá celou dávku receptury (kalkulace si ji dosadí).
                    // Natvrdo 1 tu dřív u receptu na 60 ks naúčtovalo celou práci jednomu kusu.
                    pocet_porci: m.pocet_porci ?? null,
                    pocet_operatoru: m.pocet_operatoru ?? 1,
                    stroj_ids,
                });
            }

            // ── Reconcile ingrediencí (working copy vs poslední serverový stav) ──
            if (bomDirtyRef.current) {
                const baseBom = bomBaselineRef.current || [];
                const workBom = bomItemsRef.current || [];
                const workIds = new Set(workBom.map(b => b.id));
                // Smazané: byly na serveru, už nejsou v pracovní kopii.
                for (const b of baseBom) {
                    if (typeof b.id === 'number' && !workIds.has(b.id)) {
                        await apiCall(`/api/recepty/kusovnik/${b.id}`, 'DELETE');
                    }
                }
                // Nové (tmp id) → POST; změněné → PUT. Mapa tmp→real pro pořadí.
                const baseById = new Map(baseBom.map(b => [b.id, b]));
                const idRemap = new Map();
                for (const b of workBom) {
                    if (typeof b.id === 'string') {
                        const created = await apiCall(`/api/recepty/recepty/${recept.id}/kusovnik/polozka`, 'POST', {
                            surovina_id: b.surovina_id ?? null,
                            ingredience_recept_id: b.ingredience_recept_id ?? null,
                            obal_id: b.obal_id ?? null,
                            spotrebni_zbozi_id: b.spotrebni_zbozi_id ?? null,
                            spotreba: b.spotreba ?? 0,
                            jednotka_id: b.jednotka_id,
                            scrap_pct: b.scrap_pct ?? 0,
                            sop_krok_id: b.sop_krok_id ?? null,
                        });
                        idRemap.set(b.id, created.id);
                    } else {
                        const base = baseById.get(b.id);
                        const changed = base && (
                            Number(base.spotreba) !== Number(b.spotreba)
                            || (base.jednotka_id ?? null) !== (b.jednotka_id ?? null)
                            || Number(base.scrap_pct ?? 0) !== Number(b.scrap_pct ?? 0)
                            || (base.sop_krok_id ?? null) !== (b.sop_krok_id ?? null)
                        );
                        if (changed) {
                            await apiCall(`/api/recepty/kusovnik/${b.id}`, 'PUT', {
                                spotreba: b.spotreba ?? 0,
                                jednotka_id: b.jednotka_id,
                                scrap_pct: b.scrap_pct ?? 0,
                                sop_krok_id: b.sop_krok_id ?? null,
                            });
                        }
                    }
                }
                // Pořadí celého kusovníku dle pracovní kopie (tmp id → real).
                const finalOrder = workBom
                    .map(b => (typeof b.id === 'string' ? idRemap.get(b.id) : b.id))
                    .filter(Boolean);
                if (finalOrder.length) {
                    await apiCall(`/api/recepty/recepty/${recept.id}/kusovnik/reorder`, 'PATCH', { poradi: finalOrder });
                }
            }

            setDraftRecept({}); setDraftSteps({}); setBomDirty(false);
            bomDirtyRef.current = false; // sync hned, ať reload níž natáhne serverová data (reálná ID)
            await loadData({ silent: true, noSpinner: true });
            fireCostChanged();
        } catch (e) {
            window.dispatchEvent(new CustomEvent('in-app-toast', {
                detail: { id: crypto.randomUUID(), title: 'Chyba ukládání', body: e?.error || e?.message || 'Zkuste to znovu' },
            }));
        } finally { setSaving(false); }
    }, [saving, isDirty, draftRecept, draftSteps, steps, recept?.id, loadData, fireCostChanged]);

    // Zpřístupnit uložení rodiči — ukládá se jedním tlačítkem Uložit nahoře na položce.
    useEffect(() => {
        if (saveRef) saveRef.current = saveAll;
        return () => { if (saveRef && saveRef.current === saveAll) saveRef.current = null; };
    }, [saveAll, saveRef]);

    // Reload po strukturálních změnách (ingredience, média, přidání/smazání kroku).
    // Draft (draftSteps) zůstává v paměti a překryje se přes čerstvý baseline.
    const reload = useCallback(() => loadData({ noSpinner: true }), [loadData]);

    // Sloučené hodnoty (baseline + draft)
    const mRecept = useMemo(() => ({ ...localRecept, ...draftRecept }), [localRecept, draftRecept]);
    const activeBaseStep = steps.find(s => s.id === activeStepId);
    const activeStep = activeBaseStep ? { ...activeBaseStep, ...(draftSteps[activeStepId] || {}) } : null;
    const neprirazenyBom = bomItems.filter(b => !b.sop_krok_id);
    const stepBom = activeStepId === NEPRIRAZENO ? neprirazenyBom : bomItems.filter(b => b.sop_krok_id === activeStepId);

    // ── Živý přepočet kalkulace nad draftem ───────────────────────────
    // Ingredience i kroky se ukládají až přes Uložit, takže serverová kalkulace
    // do té doby drží starý stav. Náhled pošle pracovní kopii a vrátí stejný
    // payload — statistiky nahoře i kalkulace kroku tak jedou v reálném čase.
    const [costsPending, setCostsPending] = useState(false);
    const previewSeq = useRef(0);
    const previewRef = useRef(onKalkulacePreview);
    previewRef.current = onKalkulacePreview;

    const draftKroky = useMemo(() => steps.map(s => {
        const m = { ...s, ...(draftSteps[s.id] || {}) };
        return {
            id: s.id,
            cas_s: m.cas_s ?? 0,
            pocet_operatoru: m.pocet_operatoru ?? 1,
            pocet_porci: m.pocet_porci ?? null,
            stroj_ids: Array.isArray(m.stroj_ids) ? m.stroj_ids : baseStrojIds(m),
        };
    }), [steps, draftSteps]);

    const draftKusovnik = useMemo(() => bomItems.map(b => ({
        key: String(b.id),
        surovina_id: b.surovina_id ?? null,
        ingredience_recept_id: b.ingredience_recept_id ?? null,
        obal_id: b.obal_id ?? null,
        spotrebni_zbozi_id: b.spotrebni_zbozi_id ?? null,
        spotreba: b.spotreba ?? 0,
        jednotka_id: b.jednotka_id ?? null,
        scrap_pct: b.scrap_pct ?? 0,
        sop_krok_id: b.sop_krok_id ?? null,
    })), [bomItems]);

    useEffect(() => {
        if (!recept?.id || !isDirty) { setCostsPending(false); return; }
        setCostsPending(true);
        const seq = ++previewSeq.current;
        const t = setTimeout(async () => {
            try {
                const data = await apiCall(`/api/recepty/recepty/${recept.id}/kalkulace-nahled`, 'POST', {
                    pocet_porci: mRecept?.pocet_porci ?? null,
                    jednotka_id: mRecept?.jednotka_id ?? null,
                    kusovnik: draftKusovnik,
                    kroky: draftKroky,
                });
                if (seq === previewSeq.current) previewRef.current?.(data);
            } catch { /* náhled je jen informativní — chybu neotravujeme toastem */ }
            if (seq === previewSeq.current) setCostsPending(false);
        }, 300);
        return () => clearTimeout(t);
    }, [recept?.id, isDirty, draftKusovnik, draftKroky, mRecept?.pocet_porci, mRecept?.jednotka_id]);

    // Odchod z editoru s neuloženým draftem — vrátit statistiky na serverový stav.
    const dirtyRef = useRef(false);
    dirtyRef.current = isDirty;
    useEffect(() => () => { if (dirtyRef.current) costChangedRef.current?.(); }, []);

    // Náklad jedné položky kusovníku — mapa podle klíče (reálné ID i tmp_N z draftu).
    const itemCosts = useMemo(() => {
        const m = new Map();
        for (const it of kalkulace?.items || []) m.set(String(it.key ?? it.id), it);
        return m;
    }, [kalkulace]);
    const materialTotal = useMemo(
        () => (kalkulace?.items || []).reduce((sum, it) => sum + (Number(it.cost) || 0), 0),
        [kalkulace],
    );
    const neprirazenoCost = neprirazenyBom.reduce((sum, b) => sum + (Number(itemCosts.get(String(b.id))?.cost) || 0), 0);

    // Ingredience receptu pro @-mention v popisu kroku
    const ingredienty = useMemo(() => {
        const seen = new Set(); const out = [];
        for (const b of bomItems) {
            const nazev = b.surovina?.nazev || b.ingredienceRecept?.nazev || b.obal?.nazev || b.spotrebniZbozi?.nazev;
            if (nazev && !seen.has(nazev)) {
                seen.add(nazev);
                out.push({ nazev, typ: b.surovina_id ? 'MAT' : (b.ingredience_recept_id ? 'SEM' : '') });
            }
        }
        return out;
    }, [bomItems]);

    const mutedColor = isDark ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)';

    useEffect(() => {
        const id = 'receptura-hide-scrollbar';
        if (!document.getElementById(id)) {
            const style = document.createElement('style');
            style.id = id;
            style.textContent = `.receptura-scroll { overflow-y: auto; scrollbar-width: none; -ms-overflow-style: none; } .receptura-scroll::-webkit-scrollbar { display: none; }`;
            document.head.appendChild(style);
        }
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12" style={{ color: mutedColor }}>
                <Loader2 size={20} className="animate-spin" />
                <span className="ml-2 text-sm">Nacitam recepturu...</span>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Header — dávka/jednotka (řízené draftem, uloží se přes Uložit nahoře) */}
            <div style={{ flexShrink: 0 }}>
                <RecipeHeader
                    recept={mRecept}
                    steps={steps}
                    bomItems={bomItems}
                    isDark={isDark}
                    jednotky={jednotky}
                    onPatchRecept={patchReceptDraft}
                />
            </div>

            {/* Main layout */}
            <div className="flex gap-5" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <div className="receptura-scroll" style={{ width: 310, flexShrink: 0 }}>
                    <StepTimeline
                        steps={steps}
                        setSteps={setSteps}
                        activeStepId={activeStepId}
                        onSelectStep={(id) => { setActiveStepId(id); setActiveTab('postup'); }}
                        isDark={isDark}
                        recept={mRecept}
                        bomItems={bomItems}
                        krokCosts={krokCosts}
                        jednotkaZkratka={kalkulace?.jednotka_zkratka}
                        draftSteps={draftSteps}
                        neprirazenoId={NEPRIRAZENO}
                        neprirazenoCost={neprirazenoCost}
                        onReload={reload}
                    />
                </div>

                {activeStepId === NEPRIRAZENO ? (
                    <div className="receptura-scroll" style={{ flex: 1, minWidth: 0 }}>
                        <NeprirazenePanel
                            bom={neprirazenyBom}
                            steps={steps}
                            isDark={isDark}
                            jednotky={jednotky}
                            itemCosts={itemCosts}
                            materialTotal={materialTotal}
                            costsPending={costsPending}
                            jednotkaZkratka={kalkulace?.jednotka_zkratka}
                            davkaMnozstvi={kalkulace?.mnozstvi}
                            onUpdateBom={updateBom}
                            onDeleteBom={deleteBom}
                            onReorderBom={reorderBom}
                            onNavigate={onNavigate}
                            onPresunout={presunNeprirazene}
                        />
                    </div>
                ) : activeStep ? (
                    <div className="receptura-scroll" style={{ flex: 1, minWidth: 0 }}>
                        <StepDetail
                            step={activeStep}
                            stepIndex={steps.findIndex(s => s.id === activeStepId)}
                            stepBom={stepBom}
                            allBomItems={bomItems}
                            setBomItems={setBomItems}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            isDark={isDark}
                            stroje={stroje}
                            jednotky={jednotky}
                            ingredienty={ingredienty}
                            recept={mRecept}
                            krokCost={krokCosts[activeStep.id]}
                            jednotkaZkratka={kalkulace?.jednotka_zkratka}
                            receptTotal={kalkulace?.total_cost}
                            itemCosts={itemCosts}
                            materialTotal={materialTotal}
                            costsPending={costsPending}
                            davkaMnozstvi={kalkulace?.mnozstvi}
                            onNavigate={onNavigate}
                            onPatchStep={(patch) => patchStepDraft(activeStepId, patch)}
                            onAddBom={addBom}
                            onUpdateBom={updateBom}
                            onDeleteBom={deleteBom}
                            onReorderBom={reorderBom}
                            onReload={reload}
                            onCostChanged={fireCostChanged}
                        />
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center" style={{ color: mutedColor }}>
                        <p className="text-sm">{steps.length === 0 ? 'Pridejte prvni krok pomoci tlacitka vlevo.' : 'Vyberte krok z timeline.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
